import { DealerInfo } from '@app-services/oem-dealer.service';
import { ValueConfig } from '@app/views/private/admin/model/advertisers';
import { AudioHelper } from '@app/views/public/plugin-raio-x/plugin-raio-x.service';
import { environment } from '@env/environment';
import { AlternativeBidRequest, ConditionalOffer, CounterOffer } from '../services/dealer.service';
import { VehicleFullDossier } from './dossier';
import { AxleAndTire, DossierHelper, EvalComment, FastQuote, SellPriceSummary, TireReport } from './evaluation';
import { MolicarPrice } from './molicar';
import { PageControl } from './page-control';

export enum DealTypeEnum {
  DEFAULT = 'Repasse',
  FAST_QUOTE = 'Cotação rápida',
  SHOWROOM = 'Showroom',
}

export enum ReportType {
  INTEGRATION = 'Integração de placas',
  PDF = 'PDF automático'
}

export enum FuelEnum {
  GASOLINE = 'Gasolina',
  ALCOHOL = 'Etanol',
  FLEX = 'Flex',
  MULTIFLEX = 'Tri-flex',
  HYBRID = 'Híbrido',
  DIESEL = 'Diesel',
  GNV = 'Gás natural (GNV)',
  GASOLINE_GNV = 'Gasolina/Gás natural (GNV)',
  ALCOHOL_GNV = 'Álcool/Gás natural (GNV)',
  ELETRIC = 'Elétrico'
}

export enum TransmissionEnum {
  MANUAL = 'Manual',
  AUTOMATIC = 'Automático',
  AUTOMATED_MANUAL = 'Automatizado',
  SEMI_AUTOMATIC = 'Semi-automático',
}

export enum Color {
  YELLOW = 'Amarelo',
  BEIGE = 'Bege',
  WHITE = 'Branco',
  BRONZE = 'Bronze',
  GREY = 'Cinza',
  BROWN = 'Marrom',
  SILVER = 'Prata',
  BLACK = 'Preto',
  GREEN = 'Verde',
  RED = 'Vermelho',
  BLUE = 'Azul',
  GOLD = 'Dourado',
  ORANGE = 'Laranja',
  PINK = 'Rosa',
  PURPLE = 'Roxo',
  MULTI_COLOR = 'Várias cores',
}

export enum CountryOrigin {
  NATIONAL = 'Nacional',
  IMPORTED = 'Importado',
}

export enum EquipmentClassification {
  GOOD = 'Bom',
  REGULAR = 'Regular',
  BAD = 'Ruim',
  NON_EXISTENT = 'N/A',
}

export enum EquipmentClassificationIcon {
  GOOD = 'check_circle',
  REGULAR = 'error',
  BAD = 'error',
  NON_EXISTENT = 'error',
}

export enum ReportEquipment {
  ABS = 'ABS',
  AIRBAG = 'Airbags',
  AIR_CONDITIONING = 'Ar condicionado',
  ARMORING = 'Blindagem',
  TRANSMISSION = 'Câmbio',
  CHASSIS = 'Chassis',
  ELECTRICS = 'Elétrica',
  BREAKS = 'Freios',
  INTERIOR = 'Interior',
  BODYWORK = 'Lataria',
  INJECTION = 'Injeção',
  ENGINE = 'Motor',
  PAINTING = 'Pintura',
  WHEELS = 'Rodas',
  TIRES = 'Pneus',
  SUSPENSION = 'Suspensão',
  GLASSES = 'Vidros',
  CARPETS = 'Tapeçaria',
  AXLE = "Eixos",
  DIFFERENTIAL = "Diferencial",
  CABIN = "Cabine",
  CATALYST = "Catalisador"
}

export function isArmoredEquipment(equipmentIds?: string[]): boolean {
  const blindadoEquipmentId = environment.blindadoEquipmentId;
  return !!blindadoEquipmentId
    && Array.isArray(equipmentIds)
    && equipmentIds.includes(blindadoEquipmentId);
}

export enum ImplementTypeName {
  TRACTOR_UNIT = 'Cavalo mecânico',
  BOX_TRUCK = 'Baú',
  INSULATED_AERIAL_LIFT = 'Cesto Aéreo Isolado',
  STEEL_TANK = 'Tanque Aço',
  DRY_CARGO = 'Carga Seca',
  CAR_CARRIER = 'Cegonha',
  CHASSIS = 'Chassi',
  CURTAIN_SIDER_TRAILER = 'Carreta Sider',
  BULK_TRAILER = 'Graneleiro',
  LOWBED_TRAILER = 'Prancha',
  LIVESTOCK_TRAILER = 'Carga Viva',
  DUMP_TRAILER = 'Basculante',
  ALUMINUM_TANK = 'Tanque Alumínio',
  STAINLESS_STEEL_TANK = 'Tanque Inox',
  SILO_TANK = 'Silo',
  TRAIN = 'Comboio',
  CRANE = 'Guindaste',
  CRANE_AUTO = 'Guindauto',
  IRRIGATION_PIPE = 'Pipa Irrigador',
  SUPPLY_PIPE = 'Pipa de Abastecimento',
  HYDRO_VACUUM = 'Hidrovácuo'
}

export enum TruckType {
  OFF_ROAD = 'Off Road',
  HIGHWAY = 'Rodoviário',
  URBAN = 'Urbano',
  VUC = 'VUC'
}
 
export class ReportEquipmentMotorcycle {
  static equipmentsMotorcycle = [ReportEquipment.TIRES, ReportEquipment.WHEELS, ReportEquipment.BREAKS,
  ReportEquipment.SUSPENSION, ReportEquipment.CHASSIS, ReportEquipment.ENGINE, ReportEquipment.INJECTION,
  ReportEquipment.BODYWORK, ReportEquipment.PAINTING, ReportEquipment.ELECTRICS, ReportEquipment.CARPETS];

  static contains(equipment: string): boolean {
    return this.equipmentsMotorcycle.includes(ReportEquipment[equipment]);
  }
}

export enum OnlineEvalReportEquipment {
  TRANSMISSION = 'Câmbio',
  INTERIOR = 'Interior',
  ENGINE = 'Motor',
  PAINTING = 'Pintura',
  WHEELS = 'Rodas',
  TIRES = 'Pneus',
  SUSPENSION = 'Suspensão',
  CARPETS = 'Tapeçaria',
}

export enum ReportStatus {
  APPROVED = 'Aprovado',
  WITH_RESTRICTIONS = 'Aprovado com apontamento',
  DISAPPROVED = 'Reprovado',
  IDENTIFIED = 'Constatação'
}

export enum ReportImagesExtractionStatus{
  WAITING = 'WAITING',
  COMPLETE = 'COMPLETE',
  ERROR = 'ERROR',
}

export enum TruckTractions {
  FOUR_BY_TWO = '4x2',
  SIX_BY_TWO = '6x2',
  SIX_BY_FOUR = '6x4',
  EIGHT_BY_TWO = '8x2',
  EIGHT_BY_FOUR = '8x4',
  TEN_BY_FOUR = '10x4',
  TEN_BY_EIGHT = '10x8'
}

export enum TruckClassifications {
  UTILITARY = 'Utilitário',
  SEMI_LIGHT = 'Semi-Leve',
  LIGHT = 'Leve',
  MEDIUM = 'Médio',
  SEMI_HEAVY = 'Semi-Pesado',
  HEAVY = 'Pesado',
  EXTRA_HEAVY = 'Extra-Pesado'
}

export enum AuctionExpandedStatus {
  NO_AUCTION = 'Não publicado',
  WAITING_PUBLICATION = "Aguardando publicação",
  WAITING_FIRST_BID = 'Aguardando oferta inicial',
  FAST_QUOTE = 'Em cotação rápida',
  FAST_QUOTE_WITH_OFFER = 'Em cotação rápida (com oferta)',
  WITH_BIDS = 'Com oferta',
  PENDING_CONDITIONAL_OFFER = 'Com oferta condicional',
  PENDING_ALTERNATIVE_BID = 'Com oferta personalizada',
  INTEGRATED_SUPERBID = 'Integrado na Superbid',
  SOLD_NO_PAYMENT = 'Vendido (fee plataforma pendente)',
  SOLD_WITH_PAYMENT = 'Vendido',
  INACTIVE = 'Inativado',
  REPUBLISHED_MULTIPLE_TIMES = 'Republicados várias vezes'
}

export enum AuctionStatus {
  NO_AUCTION = 'Não publicado',
  ACTIVE = 'Em andamento',
  SOLD = 'Vendido',
  INACTIVE = 'Inativo',
  WAITING_PUBLICATION = "Aguardando publicação"
}

export enum AuctionHistoryEventType {
  AUCTION_CREATED = 'Anúncio publicado',
  AUCTION_SCHEDULED = 'Anúncio agendado',
  BID = 'Oferta',
  CANCELLED_BID = 'Oferta cancelada',
  FINISHED_BID = 'Arremate',
  MIN_BID_CHANGE = 'Oferta mínima atualizada',
  MAX_BID_CHANGE = 'Valor para compre já atualizado',
  EXPIRATION_TIME_CHANGE = 'Horário de finalização atualizado',
  BID_OPTION_CHANGE = 'Tipo de proposta atualizado',
  INACTIVATED = 'Anúncio desativado',
  REACTIVATED = 'Anúncio reativado',
  CANCELLED = 'Anúncio cancelado',
  CANCEL_SALE = 'Venda cancelada',
  AUCTION_RENEWED = 'Anúncio renovado automaticamente',
  BID_SUGGESTION_RECEIVED = 'Sugestão de valor recebida',
  BID_SUGGESTION_IGNORED = 'Sugestão rejeitada',
  BID_SUGGESTION_ACCEPTED = 'Sugestão aceita',
  COUNTER_OFFER_CREATED = 'Contra proposta feita',
  COUNTER_OFFER_ACCEPTED = 'Contra proposta aceita pelo comprador',
  COUNTER_OFFER_REJECTED = 'Contra proposta rejeitada pelo comprador',
  COUNTER_OFFER_CANCELED = 'Contra proposta cancelada pelo vendedor',
  DELETED = 'Anúncio removido',
  RESTORED = 'Veículo restaurado',
  CREATED_STOCK_INTEGRATION = 'Veículo criado via integração de estoque',
  SYNCED_STOCK_INTEGRATION = 'Veículo sincronizado via integração de estoque',
  CONDITIONAL_OFFER_RECEIVED = 'Oferta condicional recebida',
  CONDITIONAL_OFFER_ACCEPTED = 'Oferta condicional aceita',
  CONDITIONAL_OFFER_REJECTED = 'Oferta condicional rejeitada'
}

export class SegmentLabel {
  static allSegments: SegmentLabel[] = [
    { segment: 'CAR', iconClass: 'fa-solid fa-car-side', vehicleLabel: 'Carros', vehicleLabelSingular: 'Carro', segmentLabel: 'Leves' },
    { segment: 'TRUCK', iconClass: 'fa-solid fa-truck', vehicleLabel: 'Caminhões', vehicleLabelSingular: 'Caminhão', segmentLabel: 'Pesados' },
    { segment: 'MOTORCYCLE', iconClass: 'fa-solid fa-motorcycle', vehicleLabel: 'Motos', vehicleLabelSingular: 'Moto', segmentLabel: 'Motos' }
  ];

  static allComissionSegments: SegmentLabel[] = [
    { segment: 'CAR', iconClass: 'fa-solid fa-car-side', vehicleLabel: 'Carros', vehicleLabelSingular: 'Carro', segmentLabel: 'Leves' },
    { segment: 'TRUCK', iconClass: 'fa-solid fa-truck', vehicleLabel: 'Caminhões', vehicleLabelSingular: 'Caminhão', segmentLabel: 'Pesados' },
    { segment: 'MOTORCYCLE_LOW', iconClass: 'fa-solid fa-motorcycle', vehicleLabel: 'Motos baixa cilindrada', vehicleLabelSingular: 'Moto baixa cilindrada', segmentLabel: 'Motos baixa cilindrada' },
    { segment: 'MOTORCYCLE_HIGH', iconClass: 'fa-solid fa-motorcycle', vehicleLabel: 'Motos alta cilindrada', vehicleLabelSingular: 'Moto alta cilindrada', segmentLabel: 'Motos alta cilindrada' }
  ];

  segment: string;
  iconClass: string;
  vehicleLabel: string;
  vehicleLabelSingular: string;
  segmentLabel: string;

  static getSegment(segment: string): SegmentLabel {
    return SegmentLabel.allSegments.concat(SegmentLabel.allComissionSegments).find(s => s.segment === segment);
  }

}


export class  Vehicle {
  address: string;
  addressComplement: string;
  addressNumber: string;
  alternativeBidRequests: AlternativeBidRequest[];
  allowConditionalOffer: boolean;
  audio: AudioHelper;
  automaticBidValue: number;
  autoRenewal?: boolean | null;
  axleAndTire: AxleAndTire;
  checkoutUrl?: string;
  bankSlipUrl: string;
  pixQrCode?: string;
  bidOption?: 'DEFAULT' | 'ONLY_BID' | 'ONLY_FINAL_BID' | 'NONE';
  bidStep: number;
  bidValue: number;
  bodywork: string;
  boughtByMe?: boolean;
  canCancelBid?: boolean;
  carManual: boolean;
  city: string;
  color: Color;
  comissionPaid: boolean;
  comissionValue: number;
  comissionValueConfig?: ValueConfig;
  segmentComissionConfigs?: Partial<Record<'CAR' | 'MOTORCYCLE' | 'MOTORCYCLE_LOW' | 'MOTORCYCLE_HIGH' | 'TRUCK', ValueConfig>>;
  conditionalOffer: ConditionalOffer;
  costPrice: number;
  counterOffer: CounterOffer;
  hasCounterOfferPending?: boolean;
  counterOfferValue?: number;
  countryOrigin: CountryOrigin;
  currentBidDealerName: string;
  currentPrice: number;
  dealersExclusiveGroups: string[];
  dealersWithOffer?: DealerInfo[];
  deletedAt?: Date;
  description: string;
  doors: number;
  dossier?: VehicleFullDossier;
  dossierPrice?: number;
  dossierStatus?: string;
  enabledAutomaticBid: boolean;
  equipmentIds: string[];
  equipments: string[];
  erpInvoiceData?: ErpInvoiceData;
  evalDealerName?: string;
  exclusiveDealer?: boolean;
  expiration: string;
  expirationDate: string;
  expirationTimeConfig: string;
  expirationTimeLeft: number;
  extraKey: boolean;
  fastQuote: FastQuote;
  fastQuoteId: string;
  fastQuoteStats: AuctionStats;
  favorite: boolean;
  finishValue: number;
  firstPublishedAt: Date;
  fipeDiff: number;
  fipePrice: number;
  fuel: FuelEnum;
  fullDossierDate: Date;
  futureFipePrice?: number;
  gearsSize: number;
  generalComments: EvalComment[];
  hasBids: boolean;
  hasConditionalOfferPending: boolean;
  hasDossier?: boolean;
  hasPersonalizedOffer: boolean;
  hasVideo?: boolean;
  hasWhiteLabel: boolean;
  highestBid: number;
  highestAlternativeBidValue: number;
  historyEvents: AuctionHistoryEvent[];
  id: string;
  imgUrls: string[];
  implement: Implement;
  includedValueVehicleInvoice?: boolean;
  initialBid: number;
  isAuction: number;
  isConditionalOffer?: boolean | null;
  isDamaged: boolean;
  isNew: boolean;
  isArmored: boolean;
  ignorePersonalizedOffers?: boolean | null;
  km: number;
  listPrice?: number;
  listPriceFipeDiff?: number;
  locationId: string;
  make: string;
  makeId: string;
  manufactureYear: number;
  minBidValue: number;
  minPersonalizedOfferValue: number;
  minSellPrice?: number;
  model: string;
  modelId: string;
  modelYear: number;
  motorcycleType?: string;
  monthlyStayIncrease: number;
  molicarPrices?: MolicarPrice[];
  numberPlate: string;
  observation: string;
  oemDealer: string;
  oemDealerName: string;
  oemDealerId: string;
  oemDealerImgUrl: string;
  oemDealerLocation: string;
  oemDealerLocationId: string;
  oldDossiers: DossierHelper[];
  oldFipePrices: FipePrice[];
  ownsAnyBid: boolean;
  ownsCurrentBid: boolean;
  paymentMethodGenerationPending?: boolean;
  pendingExclusiveDealer?: boolean;
  personalizedOfferStatus: string;
  personalizedOfferValue: number;
  power: number;
  publicationDate: Date;
  publicationTime: string;
  reasonDeletion?: string;
  rejectedBidValue?: number;
  report: VehicleReport;
  reportStatus: ReportStatus;
  reportImagesExtractionStatus?: ReportImagesExtractionStatus | null;
  reportImagesExtractionError?: string | null;
  segment: string;
  sellingTerms: string;
  sellingTermsOrigin?: 'VEHICLE' | 'LOCATION' | 'DEALER';
  sellPrice?: SellPriceSummary;
  slug: string;
  soldAt: Date;
  stats: AuctionStats;
  status: string;
  stayCost: number;
  stayUntil: Date;
  stockDate: Date;
  selectedPaymentMethod?: 'PIX' | 'BANK_SLIP' | 'CREDIT_CARD' | 'BALANCE';
  scheduledPublicationTimeConfig: string;
  scheduledPublicationTimeLeft: number;
  superBidHistories: VehicleSuperBidHistory[];
  superBidHistoriesCount: number;
  stockWebhookHistories: StockWebhookHistoryItem[];
  stockWebhookHistoriesCount: number;
  superBidOfferId: number;
  superBidLotId: number;
  superBidSubLotId: any;
  superBidProductId: number;
  superBidGroupOfferId: number;
  superBidOfferActive: boolean;
  superBidUpdating: boolean;
  superBidAvailableToFinancing: boolean;
  superBidFinancingEnabled: boolean;
  tankSize: number;
  target?: string;
  timesPublished: number;
  tireReports: TireReport[];
  transmission: TransmissionEnum;
  trim: string;
  trimId: string;
  truckCabin: string;
  truckClassification: string;
  truckTraction: string;
  truckType: string;
  additionalPlates?: string[];
  additionalPlatesQtd?: number;
  uf: string;
  views: number;
  removalCost: number;
  zipCode: string;
  isFastQuote: boolean;
  allowLeadGeneration?: boolean | null;
  leadGenerationCollectCpfCnpj?: boolean;
  destination?: 'SHOWROOM'  | 'TRADE_IN' | null;

  dealType: 'DEFAULT' | 'FAST_QUOTE' | 'SHOWROOM';
  allowDirectSale: boolean;

  get trimWithNoModel(): string {
    if (this.trim.startsWith(this.model + ' ')) {
      return this.trim.substring(this.model.length + 1);
    } else {
      return this.trim;
    }
  }

  get isFastQuoteActive(): boolean {
    return this.fastQuote && this.fastQuote.expiration && this.fastQuote.expirationTimeLeft > 0;
  }

  get sold(): boolean {
    return this.status === 'SOLD';
  }

  get inactive(): boolean {
    return this.status === 'NO_AUCTION' || this.status === 'INACTIVE';
  }

  get active(): boolean {
    return this.status === 'ACTIVE' || (this.dealType == 'FAST_QUOTE' && this.status === 'FAST_QUOTE');
  }

  get waitingPublication(): boolean {
    return this.status === 'WAITING_PUBLICATION';
  }

  static fuelValues(segment?: string): string[] {
    if (segment && segment == 'MOTORCYCLE') {
      return ["FLEX", "GASOLINE"];
    } else {
      return Object.keys(FuelEnum);
    }
  }
  static colorValues(): string[] {
    return Object.keys(Color).sort((c1, c2) => {
      const l1 = Color[c1];
      const l2 = Color[c2];

      if (l1 < l2) {
        return -1;
      }
      if (l1 > l2) {
        return 1;
      }
      return 0;
    });
  }

  get makeName(): string {
    return this.make;
  }

  get modelName(): string {
    return this.model;
  }

  get trimName(): string {
    return this.trim;
  }

  get reportItems(): any {
    return this.report && this.report.equipments ? this.report.equipments : undefined;
  }

  get images(): any {
    if (this.imgUrls && this.imgUrls.length > 0) {
      const result = [];
      this.imgUrls.filter(i => !i.endsWith("mp4")).forEach(i => result.push({ url: i }));
      return result;
    }

    return undefined;
  }

  get pendingConditionalOffer(): boolean {
    return this.status !== 'ACTIVE' && this.isConditionalOffer && this.ownsCurrentBid;
  }

  static transmissionValues(): string[] {
    return Object.keys(TransmissionEnum);
  }
  static countryOriginValues(): string[] {
    return Object.keys(CountryOrigin);
  }
  static reportStatusValues(): string[] {
    return Object.keys(ReportStatus);
  }

  static equipmentConditionValues(): string[] {
    return Object.keys(EquipmentClassification);
  }

  static truckTractionValues(): string[] {
    return Object.keys(TruckTractions);
  }

  static truckClassificationValues(): string[] {
    return Object.keys(TruckClassifications);
  }

  static implementTypeNameValues(): string[] {
    return Object.keys(ImplementTypeName);
  }

  static truckTypeValues(): string[] {
    return Object.keys(TruckType);
  }
}

export class Implement {
  type: string;
  typeLabel: string;
  makeId: string;
  makeName: string;
}

export class AuctionStats {
  impressions = 0;
  views = 0;
  uniqueImpressions = 0;
  uniqueViews = 0;
  favorites = 0;
  bids = 0;
  personalizedOffers = 0;
  rejectedPersonalizedOffers = 0;
  acceptedPersonalizedOffers = 0;
}

export class AuctionHistoryEvent {
  type: string;
  createdAt: Date;
  dealerId: string;
  dealerName: string;
  userName: string;
  cancelledBy?: string;
  cancellationReason?: string;
  cancellationNote?: string;
  bidValue: number;
  finishBidValue: number;
}

export class VehicleReport {
  reportUrl?: string;
  title?: string;
  equipments?: EquipmentReportMap;
}

export interface ErpInvoiceData {
  invoiceNumber?: string;
  invoiceProtocolCode?: string;
}

export type EquipmentReportMap = {
  [key in ReportEquipment]?: EquipmentReport;
};

export interface EquipmentReport {
  classification: EquipmentClassification;
  description?: string;
  fixingCost?: number;
  photosAndVideosUrls: string[];
  audio: AudioHelper;
}

export interface ResponseSearchAuctions {
  actualPage: number;
  data: Vehicle[];
  numberOfPages: number;
  totalElements: number;
}

export class FipePrice {
  indexMonth: Date;
  price: number;

  constructor(month: Date, price: number) {
    this.indexMonth = month;
    this.price = price;
  }
}

export class VehicleSuperBidHistory {
  id: string;
  vehicleId: string;
  type: SuperBidHistoryEntryType;
  details: string;
  diffs: DiffParam[];
  fieldTypes: SuperBidHistoryFieldType[];
  createdAt: Date;
}

export class StockWebhookHistoryItem {
  id: string;
  vehicleId: string;
  oemDealerId: string;
  event: StockWebhookEventType;
  webhookUrl: string;
  requestBody: string;
  httpStatus: number;
  responseBody: string;
  errorMessage: string;
  success: boolean;
  manualResend: boolean;
  resentByUserId: string;
  resentByUserName: string;
  originalHistoryId: string;
  createdAt: Date;
  showingDetails?: boolean;
}

export class StockWebhookHistoryFilter {
  pageControl = new PageControl();
  event?: StockWebhookEventType;
  success?: boolean;
}

export type StockWebhookEventType =
  'BID'
  | 'FINAL_BID'
  | 'ALTERNATIVE_BID'
  | 'SOLD'
  | 'AUCTION_CLOSED_NO_BID'
  | 'AUCTION_EXTENDED'
  | 'BID_CANCELED'
  | 'PROPOSAL_REJECTED_BY_BID'
  | 'COUNTER_PROPOSAL_REFUSED'
  | 'COMMISSION_PAID';

export const StockWebhookEventTypeLabel: Record<StockWebhookEventType, string> = {
  BID: 'Oferta',
  FINAL_BID: 'Oferta final',
  ALTERNATIVE_BID: 'Oferta personalizada',
  SOLD: 'Vendido',
  AUCTION_CLOSED_NO_BID: 'Encerrado sem oferta',
  AUCTION_EXTENDED: 'Leilão estendido',
  BID_CANCELED: 'Oferta cancelada',
  PROPOSAL_REJECTED_BY_BID: 'Proposta rejeitada por oferta',
  COUNTER_PROPOSAL_REFUSED: 'Contraproposta recusada',
  COMMISSION_PAID: 'Comissão paga',
};

export class DiffParam {
  fieldName: string;
  originalValue: any;
  newValue: any;
}

export enum SuperBidHistoryEntryType {
  CREATED = "Oferta criada",
  CREATED_STOCK = "Atualização via integração de estoque",
  UPDATED = "Oferta atualizada",
  INACTIVATED = "Oferta inativada",
  SOLD = "Veículo vendido",
  PUT_BID = "Oferta comprador adicionada",
  MISSING_MANDATORY_FIELDS = "Campos obrigatórios não preenchidos",
  INTEGRATION_ERROR = "Erro ao integrar oferta",
  PUT_BID_ERROR = "Erro ao adicionar oferta do comprador",
};

export enum SuperBidHistoryFieldType {
  MAKE = "Marca",
  MODEL = "Modelo",
  LOCATION = "Pátio",
  LOCATION_GOOGLE_MAPS = "Localização no Google Maps",
  FUEL = "Combustível",
  GROUP_OFFER = "Grupo de oferta",
}

export enum SuperBidHistoryFieldAuction {
  "superBidOfferId" = "Id da oferta",
  "superBidLotId" = "Id do lote",
  "superBidProductId" = "Id do produto",
}

export interface SimpleVehicleInfo {
  id: string;
  numberPlate: string;
  make: string;
  model: string;
  trim: string;
  modelYear: number;
  manufactureYear: number;
  imgUrl: string;
}